/**
 * Element Schema Sanitizers
 * Sanitization utilities for element values
 */

import { sanitizeWithDOMPurify } from '@/lib/dompurify'

import type { ElementType } from '@/types/element-schema.type'

// Configure DOMPurify to allow safe HTML but remove dangerous elements
const ALLOWED_TAGS = ['b', 'i', 'u', 'strong', 'em', 'span', 'p', 'br', 'a']
const ALLOWED_ATTR = ['href', 'target', 'rel', 'class']

/**
 * Sanitize text value using DOMPurify
 * Removes dangerous HTML while preserving safe formatting
 */
export function sanitizeTextValue(value: string): string {
  return sanitizeWithDOMPurify(value, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  }).trim()
}

/**
 * Validate and sanitize hex color
 */
export function sanitizeColorValue(value: string): string | null {
  if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
    return value.toLowerCase()
  }
  if (/^#[0-9A-Fa-f]{3}$/.test(value)) {
    // Convert short form to full form
    const r = value[1]
    const g = value[2]
    const b = value[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  return null
}

/**
 * Sanitize URL value - prevent javascript: and dangerous data: URLs
 */
export function sanitizeUrlValue(value: string): string {
  // Use DOMPurify to sanitize href attribute
  const sanitized = sanitizeWithDOMPurify(`<a href="${value}"></a>`, {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href'],
    ALLOW_DATA_ATTR: false,
  })

  // Extract the sanitized href
  const match = sanitized.match(/href="([^"]*)"/)
  return match ? match[1] : ''
}

/**
 * Sanitize HTML content (for rich text fields)
 * Allows more tags than text but still removes dangerous content
 */
export function sanitizeHtmlValue(value: string): string {
  return sanitizeWithDOMPurify(value, {
    ALLOWED_TAGS: [
      ...ALLOWED_TAGS,
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'code',
      'pre',
      'div',
      'article',
      'section',
      'img',
      'figure',
      'figcaption',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
    ],
    ALLOWED_ATTR: [...ALLOWED_ATTR, 'src', 'alt', 'width', 'height', 'style', 'data-mention-id', 'data-profile-id'],
    ALLOW_DATA_ATTR: false,
  })
}

/**
 * Sanitize extracted element value based on type
 */
export function sanitizeElementValue(value: unknown, type: ElementType): unknown {
  if (value === undefined || value === null) return value

  switch (type) {
    case 'text':
    case 'textarea':
      return typeof value === 'string' ? sanitizeTextValue(value) : value

    case 'color':
      if (typeof value === 'string') {
        const sanitized = sanitizeColorValue(value)
        return sanitized ?? value
      }
      return value

    case 'image':
    case 'video':
    case 'file':
      return typeof value === 'string' ? sanitizeUrlValue(value) : value

    case 'number':
      return typeof value === 'number' ? value : Number(value) || 0

    case 'boolean':
    case 'checkbox':
      return Boolean(value)

    case 'json':
      if (typeof value === 'string') {
        try {
          return JSON.parse(value)
        } catch {
          return value
        }
      }
      return value

    default:
      return value
  }
}
